<template>
  <div class="main-menu" :class="{open: menuVisible}">
    <div class="close-button" @click="hideNav">
      <img src="@/assets/icons/close.svg" class="pointer">
    </div>
    <nav>
      <ul class="menu">
        <li>
          <div v-if="!loggedIn && this.$route.name !== 'Login'" class="login-form-container">
            <LoginForm :goBackAfterLogin="false" :isNavigationForm="true" />
            <div class="d-flex justify-content-between">
              <router-link to="/forgot-password" class="menu-item d-inline-block">
                <span @click="hideNav" class="text-xs text-capitalize m-0">{{$t('auth.forgotPassword')}}</span>
              </router-link>
              <router-link to="/signup" class="menu-item d-inline-block">
                <span @click="hideNav" class="text-xs m-0">{{$t('auth.signup')}}</span>
              </router-link>
            </div>
          </div>
          <div v-else-if="loggedIn && user">
            <div class="d-flex align-items-center mb-2">
              <b-icon icon="person-fill"></b-icon>
              <router-link to="/profile" class="menu-item mb-0 ml-2 d-inline-block">
                <span @click="hideNav" class="m-0">{{user.display_name}}</span>
              </router-link>
            </div>
            <div class="d-flex align-items-center">
              <b-icon icon="box-arrow-right"></b-icon>
              <button @click="logout" class="d-block menu-item mb-0 ml-2">
                <span class="text-xs">{{$t('auth.logout')}}</span>
              </button>
            </div>
          </div>
        </li>
        <li>
          <p class="menu-label">{{$t('menu.mainMenu')}}</p>
          <localized-link :to="{name: 'Experts'}" class="menu-item internal">
            {{$t('menu.stories')}}
          </localized-link>
          <localized-link :to="{name: 'Home'}" class="menu-item internal">
            {{$t('menu.productSupport')}}
          </localized-link>
          <localized-link :to="{name: 'About'}" class="menu-item internal">
            {{$t('menu.about')}}
          </localized-link>
        </li>
        <li>
          <p class="menu-label">{{$t('menu.links')}}</p>
          <a href="https://catalogue.nissens.com/" target="_blank" class="menu-item">{{$t('menu.nissensProductCatalogue')}}</a>
          <a href="https://nissens.com" target="_blank" class="menu-item">{{$t('menu.nissensWebsite')}}</a>
          <a href="https://nissens.com/nissens-experts" target="_blank" class="menu-item">{{$t('menu.ntc')}}</a>
          <a v-if="$i18n.locale !== 'pl'" href="https://nissens.com/robin" target="_blank" class="menu-item">{{$t('menu.robin')}}</a>
        </li>
        <li>
          <p class="menu-label">{{$t('menu.info')}}</p>
          <localized-link :to="{name: 'Where To Buy'}" class="menu-item internal">
            {{$t('menu.whereToBuy')}}
          </localized-link>
          <a :href="privacyPolicyUrl" target="_blank" class="menu-item internal">
            {{$t('menu.privacyPolicy')}}
          </a>
          <a href="https://nissens.com/disclaimer" target="_blank" class="menu-item internal">
            {{$t('menu.termsOfUse')}}
          </a>
          <a href="https://nissens.com/find-location" target="_blank" class="menu-item internal">
            {{$t('menu.contact')}}
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>
<script>
import LoginForm from '@/components/LoginForm.vue';
import { PRIVACY_POLICY_URL } from '../config';

export default {
  props: {
    menuVisible: Boolean,
  },
  components: {
    LoginForm,
  },
  data() {
    return {
      privacyPolicyUrl: PRIVACY_POLICY_URL,
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.user.status.loggedIn;
    },
    user() {
      return this.$store.state.user.user;
    },
  },
  methods: {
    hideNav() {
      this.$emit('hideNav', false);
    },
    logout() {
      this.$store.dispatch('user/LOGOUT');
      if (this.$route.name === 'Profile') {
        this.$router.push(`/${this.$i18n.locale}/`);
      }
      this.hideNav();
    },
  },
  mounted() {
    const menuItems = document.querySelectorAll('.menu-item.internal');
    menuItems.forEach((item) => {
      item.addEventListener('click', () => {
        this.hideNav();
      });
    });
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.menu-item {
  color: #fff;
  text-transform: uppercase;
  font-size: 18px;
  margin-bottom: .5rem;
  margin-left: 1rem;
  &.inactive {
    color: #cacaca;
    cursor: default;
  }
}
.menu-label {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 11px;
  margin-bottom: 1rem;
  margin-top: 2rem;
}
button.menu-item {
  border: none;
  background-color: transparent;
  padding: 0;
}
.login-form {
  margin-left: 1rem;
  input {
    background-color: transparent;
    border: 1px solid rgba(255,255,255, .3);
    border-radius: 5px;
    padding: .5rem 1rem;
    &::placeholder {
      color: #cacaca;
    }
  }
}
.main-menu {
  position: fixed;
  top: 0;
  width: 100%;
  height: 100%;
  max-width: 500px;
  right: -500px;
  background-color: $secondary;
  transition: right .3s ease-in-out;
  z-index: 9999;
  @include media-breakpoint-down(sm) {
    max-width: unset;
    right: -100%;
  }
  a {
    display: block;
  }
  &.open {
    right: 0;
    overflow-y: auto;
    overflow-x: hidden;
    ul.menu {
      > li {
        animation-name: listFadeInRight;
        animation-duration: .25s;
        animation-timing-function: cubic-bezier(0,0,.2,1);
        opacity: 0;
        animation-fill-mode: forwards;
      }
      > li:nth-of-type(1) {
        animation-delay: .4s;
      }
      > li:nth-of-type(2) {
        animation-delay: .6s;
      }
      > li:nth-of-type(3) {
        animation-delay: .8s;
      }
      > li:nth-of-type(4) {
        animation-delay: 1s;
      }
      > li:nth-of-type(5) {
        animation-delay: 1.2s;
      }
    }
  }
  .close-button {
    display: inline-block;
    padding: 40px 30px;
    img {
      width: 100%;
      max-width: 20px;
    }
  }
  nav {
    ul {
      list-style-type: none;
      color: #fff;
    }
    > ul.menu {
      padding-left: 80px;
      padding-right: 30px;
      > li {
        margin-bottom: 1em;
        > ul {
          padding-top: 1em;
          padding-left: 1.5rem;
          > li {
            margin-bottom: .5em;
          }
        }
      }

    }
  }
}
.login-form-container {
  max-width: 250px;
  ::v-deep {
    .login-form {
      margin-left: 1rem;
      .input-group-prepend {
        display: none;
      }
      .password-container {
        margin-bottom: 15px;
      }
      .button-container {
        margin-bottom: 10px;
        button span {
          font-size: 14px!important;
        }
      }
      input {
        background-color: transparent;
        border: 1px solid rgba(255,255,255, .3);
        border-radius: 5px;
        padding: .5rem 1rem;
        color: white;
        &::placeholder {
          color: #ebebeb;
        }
      }
      .alert {
        padding: 0.4rem 1rem;
        span {
          font-size: 13px!important;
        }
      }
      .form-error {
        color: #d7d7d7;
      }
    }
  }
}
@include media-breakpoint-down(md) {
  .main-menu {
    &.open {
      overflow-y: auto;
    }
  }
  nav {
    max-width: 400px;
  }
  .close-button {
    padding: 20px 22px!important;
  }
  .menu {
    padding: 0px 50px!important;
  }
  .menu-item {
    font-size: 14px;
  }
  .menu-label {
    font-size: 10px;
  }
}
</style>
